import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { functions } from "../firebase";

type StaffMessageMode = "direct" | "group" | "announcement";

type StaffRecipient = {
  uid: string;
  email?: string;
  fullName: string;
  role: "teacher" | "office" | "admin" | "superAdmin";
  teacherKey?: string;
  gradeLevels: string[];
  specialAssignments: string[];
  linked: boolean;
};

function normalize(value: string) {
  return String(value || "").trim().toLowerCase();
}

function roleLabel(role: string) {
  if (role === "superAdmin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "office") return "Office";
  return "Teacher";
}

function staffSubtitle(person: StaffRecipient) {
  const tags = [
    roleLabel(person.role),
    person.email,
    person.gradeLevels?.length ? `Grades ${person.gradeLevels.join(", ")}` : "",
    person.specialAssignments?.length ? person.specialAssignments.join(", ") : "",
  ].filter(Boolean);
  return tags.join(" / ");
}

function defaultTitle(mode: StaffMessageMode, selected: StaffRecipient[]) {
  if (mode === "announcement") return "";
  const names = selected.map((person) => person.fullName || person.email || person.uid);
  if (names.length <= 2) return `Staff Message: ${names.join(", ")}`;
  return `Staff Message: ${names[0]}, ${names[1]} + ${names.length - 2} more`;
}

export function MessagingNewStaffPage() {
  const nav = useNavigate();
  const [recipients, setRecipients] = useState<StaffRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<StaffMessageMode>("direct");
  const [filter, setFilter] = useState("");
  const [selectedUids, setSelectedUids] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const fn = httpsCallable(functions, "listStaffMessagingRecipients");
        const res: any = await fn({});
        const items = Array.isArray(res?.data?.recipients) ? res.data.recipients : [];
        setRecipients(items);
      } catch (e: any) {
        setErr(e?.message || "Failed to load staff directory.");
        setRecipients([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedRecipients = useMemo(() => {
    const selected = new Set(selectedUids);
    return recipients.filter((person) => selected.has(person.uid));
  }, [recipients, selectedUids]);

  const filteredRecipients = useMemo(() => {
    const q = normalize(filter);
    if (!q) return recipients;
    return recipients.filter((person) => {
      const haystack = [
        person.fullName,
        person.email,
        person.role,
        person.teacherKey,
        ...(person.gradeLevels || []),
        ...(person.specialAssignments || []),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [recipients, filter]);

  const previewTitle =
    mode === "announcement" && title.trim()
      ? `Staff Announcement: ${title.trim()}`
      : title.trim() || defaultTitle(mode, selectedRecipients);

  function selectMode(nextMode: StaffMessageMode) {
    setMode(nextMode);
    setSelectedUids([]);
    setTitle("");
    setErr(null);
  }

  function toggleRecipient(uid: string) {
    setSelectedUids((prev) => {
      if (mode === "direct") return prev.includes(uid) ? [] : [uid];
      return prev.includes(uid) ? prev.filter((item) => item !== uid) : [...prev, uid];
    });
  }

  async function openThread() {
    setErr(null);

    if (!selectedUids.length) {
      setErr("Select at least one staff recipient.");
      return;
    }
    if (mode === "direct" && selectedUids.length !== 1) {
      setErr("1:1 staff messages require exactly one recipient.");
      return;
    }
    if (mode === "announcement" && !title.trim()) {
      setErr("Staff announcements require a title.");
      return;
    }
    if (mode === "announcement" && !message.trim()) {
      setErr("Staff announcements require a message.");
      return;
    }

    setBusy(true);
    try {
      const fn = httpsCallable(functions, "createStaffThread");
      const res: any = await fn({
        mode,
        recipientUids: selectedUids,
        title: title.trim(),
        message: message.trim(),
      });
      const threadId = String(res?.data?.threadId || "");
      if (!threadId) throw new Error("createStaffThread did not return a threadId.");
      nav(`/messaging?threadId=${encodeURIComponent(threadId)}`);
    } catch (e: any) {
      setErr(e?.message || "Failed to create/open staff message.");
    } finally {
      setBusy(false);
    }
  }

  const canOpen =
    selectedUids.length > 0 &&
    (mode !== "direct" || selectedUids.length === 1) &&
    (mode !== "announcement" || (title.trim().length > 0 && message.trim().length > 0));

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h2 style={{ marginTop: 0 }}>New Staff Message</h2>
          <p className="muted" style={{ marginTop: 6 }}>
            Start a staff 1:1, reusable group conversation, or one-way staff announcement.
          </p>
        </div>
        <button className="btn secondary" onClick={() => nav("/messaging")}>
          Back to Messaging
        </button>
      </div>

      {err ? <div className="error">{err}</div> : null}

      <div className="segmented-control" style={{ marginTop: 16 }} aria-label="Staff message type">
        {[
          ["direct", "1:1"],
          ["group", "Group"],
          ["announcement", "Announcement"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={mode === value ? "active" : ""}
            onClick={() => selectMode(value as StaffMessageMode)}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) minmax(280px, 1fr)", gap: 16, marginTop: 16 }}>
        <div className="messaging-compose-panel">
          <h3 style={{ marginTop: 0 }}>Staff Directory</h3>
          <input
            className="input"
            placeholder="Search name, email, role, grade, or assignment..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            {selectedUids.length} selected / {filteredRecipients.length} of {recipients.length} linked staff
          </div>
          <hr className="hr" />

          <div style={{ maxHeight: 560, overflow: "auto", display: "grid", gap: 8 }}>
            {loading ? (
              <div className="muted">Loading staff directory...</div>
            ) : filteredRecipients.length === 0 ? (
              <div className="muted">No linked staff found.</div>
            ) : (
              filteredRecipients.map((person) => {
                const selected = selectedUids.includes(person.uid);
                return (
                  <button
                    key={person.uid}
                    type="button"
                    onClick={() => toggleRecipient(person.uid)}
                    className={`messaging-choice ${selected ? "selected" : ""}`}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ fontWeight: 800 }}>{person.fullName || person.email || person.uid}</div>
                      <div className="messaging-choice-action">
                        {selected ? "Selected" : mode === "direct" ? "Choose" : "Add"}
                      </div>
                    </div>
                    <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                      {staffSubtitle(person)}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="messaging-compose-panel">
          <h3 style={{ marginTop: 0 }}>Message Details</h3>
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <div className="label">Selected Recipients</div>
              {selectedRecipients.length ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {selectedRecipients.map((person) => (
                    <button
                      key={person.uid}
                      type="button"
                      className="btn small secondary"
                      onClick={() => toggleRecipient(person.uid)}
                    >
                      {person.fullName || person.email || person.uid} x
                    </button>
                  ))}
                </div>
              ) : (
                <div className="muted" style={{ marginTop: 8 }}>No recipients selected.</div>
              )}
            </div>

            {mode === "announcement" ? (
              <label className="muted" style={{ display: "grid", gap: 6 }}>
                Staff message title
                <input
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Lunch coverage, dismissal update, staff meeting..."
                />
              </label>
            ) : null}

            <label className="muted" style={{ display: "grid", gap: 6 }}>
              {mode === "announcement" ? "Announcement message" : "Opening message optional"}
              <textarea
                className="input"
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  mode === "announcement"
                    ? "Type the announcement staff will receive..."
                    : "Type the first message, or leave blank and write it after the thread opens..."
                }
              />
            </label>

            <div className="messaging-preview-box">
              <div className="label">Thread Preview</div>
              <div style={{ fontWeight: 900, marginTop: 6 }}>
                {previewTitle || (mode === "announcement" ? "Staff Announcement: Add a title" : "Staff Message: Select recipients")}
              </div>
              <div className="muted" style={{ marginTop: 6 }}>
                {mode === "direct" ? "Reusable 1:1 staff thread" : mode === "group" ? "Reusable staff group thread with replies" : "One-way staff announcement; replies are disabled"}
              </div>
              <div className="muted" style={{ marginTop: 6 }}>
                {mode === "announcement" ? "Creates a new titled announcement thread." : "Replies go to everyone."}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
        <button className="btn secondary" onClick={() => nav("/messaging")}>
          Cancel
        </button>
        <button className="btn" disabled={busy || !canOpen} onClick={openThread}>
          {busy ? "Opening..." : "Open Staff Thread"}
        </button>
      </div>
    </div>
  );
}
